import React from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/joy";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import MailRoundedIcon from "@mui/icons-material/MailRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";

import PageHero from "../components/common/PageHero";
import { downloadDonationReceipt, fetchDonationById } from "../api";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const formatDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

interface DonationRecord {
  id: string;
  donorId?: number | null;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  pan?: string;
  date?: string;
  amount?: number;
  paymentMethod?: string;
  purpose?: string;
  head?: string;
  subPurpose?: string;
  event?: string;
  txnRefId?: string;
  taxReceiptNo?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface DetailItemProps {
  label: string;
  value?: React.ReactNode;
  icon?: React.ReactNode;
}

function DetailItem({ label, value = "—", icon }: DetailItemProps) {
  return (
    <Stack spacing={0.25}>
      <Typography level="body-xs" sx={{ textTransform: "uppercase", letterSpacing: 0.6, color: "text.tertiary" }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.75} alignItems="center">
        {icon ? (
          <Box sx={{ color: "primary.500", display: "inline-flex" }}>
            {icon}
          </Box>
        ) : null}
        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
          {value || "—"}
        </Typography>
      </Stack>
    </Stack>
  );
}

export default function DonationDetails() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const donationId = searchParams.get("id");

  const [donation, setDonation] = React.useState<DonationRecord | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [downloading, setDownloading] = React.useState(false);

  React.useEffect(() => {
    if (!donationId) {
      setError("Donation ID is missing. Please access this page from the donations list.");
      setLoading(false);
      return;
    }

    const loadDonation = async () => {
      try {
        setLoading(true);
        const data = (await fetchDonationById(donationId)) as any;
        setDonation({
          id: data?.id?.toString() ?? donationId,
          donorId: data?.donorId ? Number(data.donorId) : null,
          name: data?.name ?? "",
          email: data?.email ?? "",
          phone: data?.phone ?? "",
          address: data?.address ?? "",
          city: data?.city ?? "",
          state: data?.state ?? "",
          country: data?.country ?? "",
          pincode: data?.pincode ?? "",
          pan: data?.pan ?? "",
          date: data?.date ?? "",
          amount: Number(data?.amount ?? 0),
          paymentMethod: data?.paymentMethod ?? "",
          purpose: data?.purpose ?? "",
          head: data?.head ?? "",
          subPurpose: data?.subPurpose ?? "",
          event: data?.event ?? "",
          txnRefId: data?.txnRefId ?? "",
          taxReceiptNo: data?.taxReceiptNo ?? "",
          status: data?.status ?? "",
          createdAt: data?.createdAt ?? "",
          updatedAt: data?.updatedAt ?? "",
        });
        setError(null);
      } catch (err) {
        console.error("Failed to load donation", err);
        setError("We were unable to load this donation. Please return to the donations list and try again.");
      } finally {
        setLoading(false);
      }
    };

    loadDonation();
  }, [donationId]);

  const handleDownload = async () => {
    if (!donationId) return;
    try {
      setDownloading(true);
      const blob = await downloadDonationReceipt(Number(donationId));
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `donation-receipt-${donationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download donation receipt", err);
      setError("Unable to download the donation receipt. Please try again in a moment.");
    } finally {
      setDownloading(false);
    }
  };

  const formattedAddress = [donation?.address, donation?.city, donation?.state, donation?.country]
    .filter(Boolean)
    .join(", ");

  return (
    <Stack spacing={3} sx={{ pb: 2 }}>
      <PageHero
        eyebrow="Offering record"
        title={donation ? `Donation #${donation.id}` : "Donation details"}
        description="Review the complete devotion record including seva purpose, payment information, and devotee contact details."
        actions={[
          <Button
            key="back"
            variant="plain"
            color="neutral"
            component={RouterLink}
            to="/donations"
            startDecorator={<ArrowBackRoundedIcon />}
          >
            Back to donations
          </Button>,
          <Button
            key="receipt"
            variant="soft"
            color="primary"
            startDecorator={<DownloadRoundedIcon />}
            loading={downloading}
            disabled={!donationId}
            onClick={handleDownload}
          >
            Download receipt
          </Button>,
        ]}
      />

      {error ? (
        <Alert
          color="danger"
          variant="soft"
          sx={{ borderRadius: "lg" }}
          endDecorator={
            <Button size="sm" variant="plain" color="danger" onClick={() => navigate("/donations")}>
              Return to list
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2.5} alignItems="stretch">
        <Grid xs={12} lg={7}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: "xl",
              p: { xs: 2.5, md: 3 },
              height: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {loading ? (
              <Stack spacing={1.5}>
                <Skeleton level="h2" width="40%" />
                <Skeleton level="body-sm" width="60%" />
                <Skeleton level="body-sm" width="80%" />
                <Skeleton level="body-sm" width="55%" />
              </Stack>
            ) : donation ? (
              <>
                <Stack spacing={1}>
                  <Typography level="title-md" sx={{ fontWeight: 700 }}>
                    Offering summary
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography level="h2" sx={{ fontWeight: 800 }}>
                      {currencyFormatter.format(Number(donation.amount ?? 0))}
                    </Typography>
                    <Chip variant="soft" color="primary" startDecorator={<VolunteerActivismRoundedIcon fontSize="small" />}>
                      {donation.purpose || "Purpose not specified"}
                    </Chip>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "text.tertiary" }}>
                    <CalendarTodayRoundedIcon fontSize="small" />
                    <Typography level="body-sm">{formatDate(donation.date)}</Typography>
                  </Stack>
                </Stack>

                <Grid container spacing={2} columns={{ xs: 12, md: 12 }}>
                  <Grid xs={12} md={6}>
                    <DetailItem
                      label="Payment method"
                      value={donation.paymentMethod || "—"}
                      icon={<AccountBalanceWalletRoundedIcon fontSize="small" />}
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <DetailItem label="Transaction reference" value={donation.txnRefId || "—"} icon={<ReceiptLongRoundedIcon fontSize="small" />} />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <DetailItem label="Seva head" value={donation.head || donation.subPurpose || "—"} icon={<VolunteerActivismRoundedIcon fontSize="small" />} />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <DetailItem label="Event" value={donation.event || "—"} icon={<CalendarTodayRoundedIcon fontSize="small" />} />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <DetailItem label="Receipt number" value={donation.taxReceiptNo || "—"} icon={<ReceiptLongRoundedIcon fontSize="small" />} />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <DetailItem label="Status" value={donation.status || "Completed"} />
                  </Grid>
                </Grid>
              </>
            ) : null}
          </Card>
        </Grid>

        <Grid xs={12} lg={5}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: "xl",
              p: { xs: 2.5, md: 3 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
              height: "100%",
            }}
          >
            {loading ? (
              <Stack spacing={1.5}>
                <Skeleton level="title-md" width="50%" />
                <Skeleton level="body-sm" width="70%" />
                <Skeleton level="body-sm" width="60%" />
              </Stack>
            ) : donation ? (
              <>
                <Typography level="title-md" sx={{ fontWeight: 700 }}>
                  Devotee contact
                </Typography>
                <Stack spacing={1.5}>
                  <DetailItem label="Name" value={donation.name || "—"} icon={<PersonRoundedIcon fontSize="small" />} />
                  <DetailItem label="Email" value={donation.email || "—"} icon={<MailRoundedIcon fontSize="small" />} />
                  <DetailItem label="Phone" value={donation.phone || "—"} icon={<PhoneRoundedIcon fontSize="small" />} />
                  <DetailItem label="Address" value={formattedAddress || "—"} icon={<HomeRoundedIcon fontSize="small" />} />
                  <DetailItem label="PIN code" value={donation.pincode || "—"} />
                  <DetailItem label="PAN" value={donation.pan || "—"} icon={<BadgeRoundedIcon fontSize="small" />} />
                </Stack>
              </>
            ) : null}
          </Card>
        </Grid>

        <Grid xs={12}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: "xl",
              p: { xs: 2.5, md: 3 },
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              alignItems: { md: "center" },
              justifyContent: "space-between",
            }}
          >
            {loading ? (
              <Skeleton level="body-sm" width="40%" />
            ) : donation ? (
              <>
                <Stack spacing={0.5}>
                  <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                    Audit trail
                  </Typography>
                  <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                    Created {formatDateTime(donation.createdAt)} · Last updated {formatDateTime(donation.updatedAt)}
                  </Typography>
                </Stack>
                <Button variant="plain" color="neutral" onClick={() => navigate(`/donations?highlight=${donation.id}`)}>
                  Highlight in ledger
                </Button>
              </>
            ) : null}
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
